import cx from 'classnames';
import { size } from 'lodash';
import * as React from 'react';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Image from 'components/Image';
import Link from 'components/Link';
import ModalTrigger from 'components/ModalTrigger';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';
import EngagementRatings, { EngagementRating } from 'organizations/constants/EngagementRatings';
import OrganizationEngagementSchoolsModal from 'organizations/components/OrganizationEngagementSchoolsModal';
import { Schools } from 'organizations/components/OrganizationEngagementView/OrganizationEngagementViewDataContainer';

import OrganizationEngagementViewHeading from './OrganizationEngagementViewHeading';

import './OrganizationEngagementViewEngagementBox.scss';

const SCHOOLS_PREVIEW_COUNT = 3;

type Props = {
  eventKey: string;
  name?: string;
  schoolRating?: EngagementRating | undefined;
  schools: Schools | undefined;
  schoolsActivePercent: number;
  schoolsEmergingPercent: number;
  schoolsEngagedPercent: number;
  schoolsGrowingPercent: number;
  utmParams: string;
};

export default function OrganizationEngagementViewEngagementBox({
  eventKey,
  name,
  schoolRating,
  schools,
  schoolsActivePercent,
  schoolsEmergingPercent,
  schoolsEngagedPercent,
  schoolsGrowingPercent,
  utmParams,
}: Props) {
  return (
    <Box className="OrganizationEngagementViewEngagementBox">
      <BoxBody>
        <div className="OrganizationEngagementViewEngagementBox-inner">
          <Text
            align="left"
            element="section"
            className="OrganizationEngagementViewEngagementBox-inner-body">
            <OrganizationEngagementViewHeading>
              School engagement across {name}
            </OrganizationEngagementViewHeading>
            <Text element="p" size="medium">
              {schoolRating === 'emerging' && (
                <React.Fragment>
                  <b>{schoolsEmergingPercent}%</b> of schools in the district have the engagement
                  rating <b>Emerging</b>, compared to <b>57%</b> of schools nationwide.
                </React.Fragment>
              )}
              {schoolRating === 'growing' && (
                <React.Fragment>
                  <b>{schoolsGrowingPercent}%</b> of schools in the district have the engagement
                  rating <b>Growing</b>, compared to <b>20%</b> of schools nationwide.
                </React.Fragment>
              )}
              {schoolRating === 'active' && (
                <React.Fragment>
                  <b>{schoolsActivePercent}%</b> of schools in the district have the engagement
                  rating <b>Active</b>, compared to <b>14%</b> of schools nationwide.
                </React.Fragment>
              )}
              {schoolRating === 'engaged' && (
                <React.Fragment>
                  <b>{schoolsEngagedPercent}%</b> of schools in the district have the engagement
                  rating <b>Engaged</b>, compared to <b>9%</b> of schools nationwide.
                </React.Fragment>
              )}
            </Text>
            {schools && (
              <Row>
                <Text element="div" size="small">
                  {schools.slice(0, SCHOOLS_PREVIEW_COUNT).map((item, index: number) => (
                    <Row flush={index === 2} key={item.slug} size="xxsmall">
                      <Link to={`/school/${item.slug}`} weight="semibold">
                        {item.name}
                      </Link>
                    </Row>
                  ))}
                </Text>
              </Row>
            )}
            {size(schools) > SCHOOLS_PREVIEW_COUNT && (
              <ModalTrigger
                modal={<OrganizationEngagementSchoolsModal name={name} schools={schools} />}>
                <Button size="medium" eventKey={`${eventKey}.content_footer.cta.click`}>
                  See all {size(schools)} schools
                </Button>
              </ModalTrigger>
            )}
          </Text>
          <Text
            align="center"
            element="div"
            size="small"
            className="OrganizationEngagementViewEngagementBox-inner-figure">
            <Row size="small">
              <div className="OrganizationEngagementViewEngagementBox-chart">
                {EngagementRatings.RATINGS.map(item => {
                  let percentage = schoolsEmergingPercent;

                  if (item === 'growing') {
                    percentage = schoolsGrowingPercent;
                  } else if (item === 'active') {
                    percentage = schoolsActivePercent;
                  } else if (item === 'engaged') {
                    percentage = schoolsEngagedPercent;
                  }

                  return (
                    <div key={item} className="OrganizationEngagementViewEngagementBox-barWrap">
                      <div className="OrganizationEngagementViewEngagementBox-bar">
                        <div className="OrganizationEngagementViewEngagementBox-bar-spacer" />
                        <div
                          style={{ height: `${percentage}%` }}
                          className={cx(
                            'OrganizationEngagementViewEngagementBox-bar-amount',
                            `OrganizationEngagementViewEngagementBox-bar-amount--${item}`
                          )}>
                          <Text
                            weight="semibold"
                            className={cx(
                              'OrganizationEngagementViewEngagementBox-bar-label',
                              `OrganizationEngagementViewEngagementBox-bar-label--${item}`
                            )}>
                            {percentage}%
                          </Text>
                        </div>
                      </div>
                      <Image src={EngagementRatings.BADGES[item].svg} width={42} />
                    </div>
                  );
                })}
              </div>
            </Row>
            School engagement ratings
          </Text>
        </div>
      </BoxBody>
    </Box>
  );
}
